<script setup lang="ts">
/**
 * THE header of a Connect app's main column (the `header` slot of
 * NxWorkspace), so every app shows the same head: an NxPageHeader in compact
 * density with
 *
 * - `leading`: the sidebar toggle (`aria-expanded` = `sidebarOpen`),
 * - the title, an optional kicker and the subtitle as a polite status line,
 * - the actions in a fixed order: [`actions-start` slot] · refresh (spinner
 *   while `refreshing`) · „more“ menu (`menuItems`) · settings
 *   (`aria-pressed` = `settingsActive`) · aside toggle (`aria-expanded` =
 *   `asideOpen`) · [`actions-end` slot] · the primary create button,
 * - `toolbar`: the row under the head (search, period navigator, view switch).
 *
 * Each built-in action hides by prop; an empty `primaryLabel` means no primary
 * button. With `primaryInSidebar` the app's NxAppSidebar carries the primary
 * action (its `primary` prop): the header then shows its primary button only
 * while the sidebar does not show — collapsed, or a closed drawer in the narrow
 * mode. It reads that from the NxWorkspace it sits in (outside one, from
 * `sidebarOpen`). In a narrow header the primary button shows its icon only (the label
 * stays its accessible name). All controls are height `s`; all labels are props
 * with German defaults.
 */
import PanelLeftClose from 'lucide-vue-next/dist/esm/icons/panel-left-close.js'; import PanelLeftOpen from 'lucide-vue-next/dist/esm/icons/panel-left-open.js'; import PanelRightClose from 'lucide-vue-next/dist/esm/icons/panel-right-close.js'; import PanelRightOpen from 'lucide-vue-next/dist/esm/icons/panel-right-open.js'; import Plus from 'lucide-vue-next/dist/esm/icons/plus.js'; import RefreshCw from 'lucide-vue-next/dist/esm/icons/refresh-cw.js'; import Settings from 'lucide-vue-next/dist/esm/icons/settings.js';








import { computed, inject, type Component } from 'vue';
import NxButton from '../Button/NxButton.vue';
import NxContextMenuItem from '../ContextMenu/NxContextMenuItem.vue';
import NxContextMenuLabel from '../ContextMenu/NxContextMenuLabel.vue';
import NxContextMenuSeparator from '../ContextMenu/NxContextMenuSeparator.vue';
import NxIconButton from '../IconButton/NxIconButton.vue';
import NxMenuButton from '../Menu/NxMenuButton.vue';
import NxPageHeader from '../PageHeader/NxPageHeader.vue';
import { nxWorkspaceRegionKey } from './context';

export interface NxWorkspaceHeaderMenuItem {
  /** Emitted with `menu-select`. */
  id: string;
  label: string;
  /** Leading icon component (e.g. a lucide icon). */
  icon?: Component;
  danger?: boolean;
  disabled?: boolean;
  /** Draw a separator line above this item. */
  separatorBefore?: boolean;
  /**
   * `true`/`false` makes a checkable entry with a tick (a view option that is
   * on, the chosen one of a group); unset is a plain action.
   */
  checked?: boolean;
  /** Small heading above this item that starts a group („Lesebereich“). */
  groupLabel?: string;
}

const props = withDefaults(
  defineProps<{
    title: string;
    /** Status line under the title (a polite live region). */
    subtitle?: string;
    /** Small line above the title. */
    kicker?: string;
    /** Heading level of the title. Defaults to 1. */
    level?: 1 | 2 | 3;

    showSidebarToggle?: boolean;
    sidebarOpen?: boolean;
    sidebarToggleLabel?: string;
    /** `aria-controls` of the sidebar toggle (the sidebar's id), if known. */
    sidebarControls?: string;

    showRefresh?: boolean;
    /** Shows the refresh button's spinner and ignores its clicks. */
    refreshing?: boolean;
    refreshLabel?: string;

    /** Items of the „more“ menu; the menu is hidden without items. */
    menuItems?: NxWorkspaceHeaderMenuItem[];
    menuLabel?: string;

    showSettings?: boolean;
    /** The settings view is open (`aria-pressed`). */
    settingsActive?: boolean;
    settingsLabel?: string;

    showAsideToggle?: boolean;
    asideOpen?: boolean;
    asideToggleLabel?: string;
    /** `aria-controls` of the aside toggle (the aside's id), if known. */
    asideControls?: string;

    /** Label of the primary create button; empty = no primary button. */
    primaryLabel?: string;
    primaryDisabled?: boolean;
    /** Tooltip of the primary button (e.g. why it is disabled). */
    primaryTitle?: string;
    /**
     * The sidebar carries the primary action: show the header's primary button
     * only while the sidebar does not show.
     */
    primaryInSidebar?: boolean;
  }>(),
  {
    subtitle: '',
    kicker: '',
    level: 1,
    showSidebarToggle: true,
    sidebarOpen: true,
    sidebarToggleLabel: 'Seitenleiste ein-/ausblenden',
    sidebarControls: undefined,
    showRefresh: true,
    refreshing: false,
    refreshLabel: 'Aktualisieren',
    menuItems: () => [],
    menuLabel: 'Weitere Aktionen',
    showSettings: true,
    settingsActive: false,
    settingsLabel: 'Einstellungen',
    showAsideToggle: true,
    asideOpen: false,
    asideToggleLabel: 'Seitenbereich ein-/ausblenden',
    asideControls: undefined,
    primaryLabel: '',
    primaryDisabled: false,
    primaryTitle: undefined,
    primaryInSidebar: false,
  }
);

const emit = defineEmits<{
  'toggle-sidebar': [];
  'toggle-aside': [];
  refresh: [];
  'open-settings': [];
  primary: [];
  'menu-select': [id: string];
}>();

const workspaceRegion = inject(nxWorkspaceRegionKey, null);

/** The sidebar shows: from the workspace frame, else from the `sidebarOpen` prop. */
const sidebarShows = computed(() => workspaceRegion?.sidebarVisible?.value ?? props.sidebarOpen);

const showPrimary = computed(
  () => !!props.primaryLabel && !(props.primaryInSidebar && sidebarShows.value)
);
</script>

<template>
  <div class="nx-workspace-header" data-testid="nx-workspace-header">
    <NxPageHeader
      :title="props.title"
      :subtitle="props.subtitle"
      :kicker="props.kicker"
      :level="props.level"
      density="compact"
      live-subtitle
    >
      <template v-if="props.showSidebarToggle || $slots.leading" #leading>
        <NxIconButton
          v-if="props.showSidebarToggle"
          height="s"
          :title="props.sidebarToggleLabel"
          :aria-label="props.sidebarToggleLabel"
          :aria-expanded="props.sidebarOpen"
          :aria-controls="props.sidebarControls"
          data-testid="nx-workspace-header-sidebar-toggle"
          @click="emit('toggle-sidebar')"
        >
          <component
            :is="props.sidebarOpen ? PanelLeftClose : PanelLeftOpen"
            class="nxt-icon-md"
            aria-hidden="true"
          />
        </NxIconButton>
        <slot name="leading" />
      </template>

      <template v-if="$slots.default" #default>
        <slot />
      </template>

      <template #actions>
        <slot name="actions-start" />
        <NxIconButton
          v-if="props.showRefresh"
          height="s"
          :loading="props.refreshing"
          :title="props.refreshLabel"
          :aria-label="props.refreshLabel"
          data-testid="nx-workspace-header-refresh"
          @click="emit('refresh')"
        >
          <RefreshCw class="nxt-icon-md" aria-hidden="true" />
        </NxIconButton>
        <NxMenuButton
          v-if="props.menuItems.length > 0"
          :label="props.menuLabel"
          data-testid="nx-workspace-header-menu"
        >
          <template v-for="(item, index) in props.menuItems" :key="item.id">
            <NxContextMenuSeparator v-if="item.separatorBefore && index > 0" />
            <NxContextMenuLabel v-if="item.groupLabel">{{ item.groupLabel }}</NxContextMenuLabel>
            <NxContextMenuItem
              :danger="item.danger"
              :disabled="item.disabled"
              :checked="item.checked"
              :data-menu-item-id="item.id"
              @select="emit('menu-select', item.id)"
            >
              <template v-if="item.icon" #icon>
                <component :is="item.icon" class="nxt-icon-sm" />
              </template>
              {{ item.label }}
            </NxContextMenuItem>
          </template>
        </NxMenuButton>
        <NxIconButton
          v-if="props.showSettings"
          height="s"
          :title="props.settingsLabel"
          :aria-label="props.settingsLabel"
          :pressed="props.settingsActive"
          data-testid="nx-workspace-header-settings"
          @click="emit('open-settings')"
        >
          <Settings class="nxt-icon-md" aria-hidden="true" />
        </NxIconButton>
        <NxIconButton
          v-if="props.showAsideToggle"
          height="s"
          :title="props.asideToggleLabel"
          :aria-label="props.asideToggleLabel"
          :aria-expanded="props.asideOpen"
          :aria-controls="props.asideControls"
          data-testid="nx-workspace-header-aside-toggle"
          @click="emit('toggle-aside')"
        >
          <component
            :is="props.asideOpen ? PanelRightClose : PanelRightOpen"
            class="nxt-icon-md"
            aria-hidden="true"
          />
        </NxIconButton>
        <slot name="actions-end" />
        <NxButton
          v-if="showPrimary"
          height="s"
          class="nx-workspace-header__primary"
          :disabled="props.primaryDisabled"
          :title="props.primaryTitle"
          data-testid="nx-workspace-header-primary"
          @click="emit('primary')"
        >
          <slot name="primary-icon">
            <Plus class="nxt-icon-sm" aria-hidden="true" />
          </slot>
          <span class="nx-workspace-header__primary-label">{{ props.primaryLabel }}</span>
        </NxButton>
      </template>

      <template v-if="$slots.toolbar" #toolbar>
        <slot name="toolbar" />
      </template>
    </NxPageHeader>
  </div>
</template>

<style scoped>
.nx-workspace-header {
  container-type: inline-size;
  flex: 0 0 auto;
  min-width: 0;
  padding: var(--nxt-space-md) var(--nxt-space-lg);
  border-bottom: var(--nxt-hairline) solid var(--nxt-border);
  background: var(--nxt-surface-bg);
}

@media (max-width: 48em) {
  .nx-workspace-header {
    padding: var(--nxt-space-sm);
  }
}

@container (max-width: 40em) {
  .nx-workspace-header__primary {
    padding-inline: var(--nxt-space-xs);
  }

  /* Icon only; the label stays the button's accessible name. */
  .nx-workspace-header__primary-label {
    position: absolute;
    width: var(--nxt-hairline);
    height: var(--nxt-hairline);
    margin: calc(var(--nxt-hairline) * -1);
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
  }
}
</style>
